import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, paxRoute, sessionRequired } from "../../common";
import { AccountDeletion, AccountDeletionResponse, RequestAccountDeletionBody } from "./schemas";

export const service = {
  group: "accounts",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Accounts API",
  description: "Deleting your own account, now or after 30 days to change your mind.",
} as const satisfies ContractService;

/** How recently the session must have signed in for its owner to ask for deletion. */
export const DELETION_SIGN_IN_WINDOW_MINUTES = 10;

/** How long a requested deletion waits before the account is purged. */
export const DELETION_GRACE_DAYS = 30;

/**
 * The error a sign-in answers with when its email belonged to a deleted account. Its description is
 * `requested=<ISO time>&deleted=<ISO time>`: when the owner asked to delete it, and when it was.
 */
export const ACCOUNT_DELETED_SIGN_IN_ERROR = "ACCOUNT_DELETED";

export const getDeletion = oc
  .meta(
    paxRoute({
      auth: { kind: "session" },
      minimumTier: null,
      cost: "read",
      openToWaitlist: true,
    }),
    openapi({
      method: "GET",
      path: "/api/accounts/v1alpha1/deletion",
      operationId: "accounts.deletion.get",
      summary: "Get my account's pending deletion.",
      description:
        "Returns the deletion the caller asked for and when it purges the account, or null when none is pending.",
      inputStructure: "detailed",
      successDescription: "The pending deletion, if any.",
    }),
  )
  .errors({ ...sessionRequired })
  .output(AccountDeletionResponse);

export const requestDeletion = oc
  .meta(
    paxRoute({
      auth: { kind: "session" },
      minimumTier: "normal",
      cost: "write",
      openToWaitlist: true,
    }),
    openapi({
      method: "POST",
      path: "/api/accounts/v1alpha1/deletion",
      operationId: "accounts.deletion.create",
      summary: "Delete my account.",
      description: `Purges the caller's account now, or schedules the purge ${DELETION_GRACE_DAYS} days out, and signs out every other session. Deleting now ends this session too. Connected accounts must have signed in within the last ${DELETION_SIGN_IN_WINDOW_MINUTES} minutes; guests use their current session without signing in again. Any App Store subscription must already be set not to renew. Asking for \`now\` while a deletion is pending moves it up; asking for \`after_grace_period\` again keeps the one pending.`,
      inputStructure: "detailed",
      successDescription: "The deletion, with its purge started.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    SIGN_IN_AGAIN: {
      status: 403,
      message: `Sign in again to delete your account. Deleting needs a sign-in from the last ${DELETION_SIGN_IN_WINDOW_MINUTES} minutes.`,
    },
    APPLE_AUTHORIZATION_INVALID: {
      status: 400,
      message:
        "Apple didn't confirm the Apple ID connected to this account. Sign in with Apple again, then delete your account.",
    },
    CANCEL_APP_STORE_SUBSCRIPTION: {
      status: 409,
      message:
        "Cancel your App Store subscription in your Apple ID settings before deleting your account.",
    },
  })
  .input(z.object({ body: RequestAccountDeletionBody }))
  .output(AccountDeletion);

export const cancelDeletion = oc
  .meta(
    paxRoute({
      auth: { kind: "session" },
      minimumTier: "suspicious",
      cost: "write",
      openToWaitlist: true,
    }),
    openapi({
      method: "DELETE",
      path: "/api/accounts/v1alpha1/deletion",
      operationId: "accounts.deletion.delete",
      summary: "Cancel my account's deletion.",
      description:
        "Withdraws the caller's pending deletion, keeping the account as it was. Succeeds when nothing is pending, and refuses once the purge has started.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "No deletion is pending.",
    }),
  )
  .errors({
    ...sessionRequired,
    PURGE_STARTED: {
      status: 409,
      message: "Your account is already being deleted, so the deletion can't be cancelled.",
    },
  });
